import './src/locales'; // Import i18n configuration before anything else
import React, { useEffect, useState } from 'react';
import { View, Text, TextInput, StyleSheet, Platform } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import * as SplashScreen from 'expo-splash-screen';
import * as Font from 'expo-font';
import {
  Poppins_400Regular,
  Poppins_500Medium,
  Poppins_600SemiBold,
  Poppins_700Bold,
} from '@expo-google-fonts/poppins';
import {
  Lora_400Regular,
  Lora_700Bold,
  Lora_400Regular_Italic,
  Lora_700Bold_Italic,
} from '@expo-google-fonts/lora';
import Toast from 'react-native-toast-message';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { AppNavigator } from './src/navigation/AppNavigator';
import { Colors, defaultTextStyle } from './src/constants';
import { SplashScreenView } from './src/components';
import { DialogProvider, useDialog, UserProvider, UserClubsProvider, UnreadCountProvider } from './src/contexts';
import { supabaseErrorHandler } from './src/config/supabaseErrorHandler';
import { authService } from './src/services/auth';
import { createLogger } from './src/utils/logger';
import { initSentry } from './src/config/sentry';
import { PostHogInitializer } from './src/components/PostHogInitializer';

const logger = createLogger('App');

// Initialize Sentry error monitoring (production only)
initSentry();

// Keep the splash screen visible while we fetch resources
SplashScreen.preventAutoHideAsync();

/**
 * Component that initializes the Supabase error handler
 * Must be inside DialogProvider to access dialog context
 */
const ErrorHandlerInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dialog = useDialog();

  useEffect(() => {
    // Initialize error handler with dialog and auth service
    supabaseErrorHandler.initialize(dialog, authService);
    logger.debug('Supabase error handler initialized');
  }, [dialog]);

  return <>{children}</>;
};

export default function App() {
  const [appIsReady, setAppIsReady] = useState(false);

  useEffect(() => {
    async function prepare() {
      const startTime = Date.now();

      try {
        // Load fonts
        await Font.loadAsync({
          Poppins_400Regular,
          Poppins_500Medium,
          Poppins_600SemiBold,
          Poppins_700Bold,
          Lora_400Regular,
          Lora_700Bold,
          Lora_400Regular_Italic,
          Lora_700Bold_Italic,
          // @expo/vector-icons fonts are automatically loaded by Expo
        });

        // Set default font for all Text components
        // @ts-ignore - React Native internal API
        const defaultProps = Text.defaultProps || {};
        // @ts-ignore
        Text.defaultProps = {
          ...defaultProps,
          style: [defaultTextStyle, defaultProps.style],
        };

        // Set default font for all TextInput components
        // @ts-ignore - React Native internal API
        const defaultInputProps = TextInput.defaultProps || {};
        // @ts-ignore
        TextInput.defaultProps = {
          ...defaultInputProps,
          style: [defaultTextStyle, defaultInputProps.style],
        };

        // Ensure splash screen shows for at least 1 second
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(1000 - elapsedTime, 0);

        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
      } catch (e) {
        console.warn('Error loading fonts:', e);
      } finally {
        // Tell the application to render
        setAppIsReady(true);
      }
    }

    prepare();
  }, []);

  // Hide splash screen as soon as app is ready (useEffect is more reliable
  // than onLayout on web, where onLayout can silently not fire)
  useEffect(() => {
    if (appIsReady) {
      SplashScreen.hideAsync().catch(() => {});
    }
  }, [appIsReady]);

  if (!appIsReady) {
    return <SplashScreenView />;
  }

  return (
    <SafeAreaProvider>
    <View style={styles.container}>
      <DialogProvider>
        <UserProvider>
          <UserClubsProvider>
            <UnreadCountProvider>
              <PostHogInitializer>
                <ErrorHandlerInitializer>
                  <AppNavigator />
                </ErrorHandlerInitializer>
              </PostHogInitializer>
            </UnreadCountProvider>
          </UserClubsProvider>
        </UserProvider>
      </DialogProvider>
      <StatusBar style="auto" />
      <Toast />
    </View>
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...Platform.select({
      web: {
        overflow: 'hidden', // Prevent body scrollbar on web
        backgroundColor: Colors.background, // Linen page ground on web
      },
    }),
  },
});
